import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo, useEffect, useCallback, useRef, useState } from 'react'
import { Button } from '@/common/ui/button'
import { RtpConfirmDialog } from '@/modules/promotion/components/RtpConfirmDialog'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Card } from '@/common/ui/card'
import { Switch } from '@/common/ui/switch'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { TriangleAlert, Wand2 } from 'lucide-react'
import { GenerateTemplateModal } from './components/GenerateTemplateModal'
import {
	DailyCheckInFormValues,
	dailyCheckInFormSchema,
	MAX_DAILY_CHECK_IN_DAYS,
	MIN_DAILY_CHECK_IN_DAYS,
} from './components/FormSchema'
import { NameField } from '../CommonField'
import { CheckInRewardType } from '@/schema'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import { DAILY_CHECK_IN_REWARD_TYPE_LABELS } from './constants'
import {
	WalletConditionSection,
	prepareWalletConditionForSubmit,
} from '@/common/components/WalletConditionSection'
import useQueryGetPresetId from '@/modules/promotion/api/hooks/useQueryGetPresetId'
import useQueryGetGameList from '@/modules/game/api/hooks/useQueryGetGameList'
import { EXCLUDED_GAME_NAMES } from '@/common/constants/excludedGames'
import { RequireConnectedLineDailyCheckIn } from './components/RequireConnectedLineDailyCheckIn'
import { GetDailyCheckInByIdResponse } from '@/modules/promotion/type'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

interface DailyCheckInFormProps {
	defaultValues?: Partial<DailyCheckInFormValues>
	onSubmit: (data: DailyCheckInFormValues) => void
	mode: 'create' | 'edit'
}

function createEmptyCheckInSetting(day: number) {
	return {
		day,
		rewardType: CheckInRewardType.CREDIT as CheckInRewardType,
		amount: undefined as unknown as number,
		minimumTurnoverAmount: undefined,
		minimumDepositAmount: undefined,
		presetId: undefined,
		gameId: undefined,
		betAmount: undefined,
	}
}

function buildInitialCheckInSettings(
	days: number,
	existingSettings?: DailyCheckInFormValues['checkInSetting'],
) {
	return Array.from({ length: days }, (_, index) => {
		const existing = existingSettings?.find((s) => s.day === index + 1)
		return existing ?? createEmptyCheckInSetting(index + 1)
	})
}

export default function DailyCheckInForm({
	defaultValues,
	onSubmit,
	mode,
}: DailyCheckInFormProps) {
	const navigate = useNavigate()
	const [showRtpConfirm, setShowRtpConfirm] = useState(false)
	const [pendingPayload, setPendingPayload] =
		useState<DailyCheckInFormValues | null>(null)
	const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)

	const initialDays = defaultValues?.days ?? MAX_DAILY_CHECK_IN_DAYS

	const form = useForm<DailyCheckInFormValues>({
		resolver: zodResolver(dailyCheckInFormSchema),
		defaultValues: {
			name: '',
			isActive: true,
			requireConnectedLine: false,
			days: initialDays,
			walletCondition: {
				minimumWithdrawCredit: undefined,
				maximumWithdrawCredit: undefined,
				turnoverRound: undefined,
				gameTypes: [],
				whitelistGameProviders: [],
				usePool: false,
				poolPresetConfigs: [],
				presetConfigs: [],
				pgHard: false,
				jokerHard: false,
				jiliHard: false,
				ppHard: false,
				nlcHard: false,
			},
			...defaultValues,
			checkInSetting: buildInitialCheckInSettings(
				initialDays,
				defaultValues?.checkInSetting,
			),
		},
	})

	// Fetch presets and games for FREE_SPIN
	const { data: presets } = useQueryGetPresetId()
	const { data: gamesData } = useQueryGetGameList({
		providerName: 'PGHARD',
		skip: 0,
		take: 1000,
	})

	// Filter out excluded games by name
	const filteredGames = useMemo(() => {
		if (!gamesData?.data) return []
		return gamesData.data.filter(
			(game) => !EXCLUDED_GAME_NAMES.includes(game.name),
		)
	}, [gamesData])

	// Keeps a snapshot of every day's settings so shrinking then growing restores data
	const settingsSnapshotRef = useRef<
		Map<number, DailyCheckInFormValues['checkInSetting'][number]>
	>(new Map())

	// Watch check-in settings to lock amount to 1 when FREE_SPIN is selected
	const checkInSettings = form.watch('checkInSetting')
	const watchedDays = form.watch('days')

	useEffect(() => {
		checkInSettings.forEach((setting, index) => {
			if (
				setting.rewardType === CheckInRewardType.FREE_SPIN &&
				setting.amount !== 1
			) {
				form.setValue(`checkInSetting.${index}.amount`, 1)
			}
		})
	}, [checkInSettings, form])

	const handleDaysChange = useCallback(
		(newDays: number) => {
			const clampedDays = Math.min(
				Math.max(newDays, MIN_DAILY_CHECK_IN_DAYS),
				MAX_DAILY_CHECK_IN_DAYS,
			)
			const currentSettings = form.getValues('checkInSetting')

			// Save current settings into snapshot before modifying
			currentSettings.forEach((setting) => {
				settingsSnapshotRef.current.set(setting.day, setting)
			})

			const newSettings = Array.from({ length: clampedDays }, (_, i) => {
				const day = i + 1
				// Prefer current form value, then snapshot, then empty
				const fromCurrent = currentSettings.find((s) => s.day === day)
				const fromSnapshot = settingsSnapshotRef.current.get(day)
				return fromCurrent ?? fromSnapshot ?? createEmptyCheckInSetting(day)
			})

			form.setValue('checkInSetting', newSettings)
			form.setValue('days', clampedDays)
		},
		[form],
	)

	const handleSubmit = async (data: DailyCheckInFormValues) => {
		const payload = {
			...data,
			walletCondition: prepareWalletConditionForSubmit(data.walletCondition),
		}

		if (mode === 'create') {
			setPendingPayload(payload)
			return
		}

		try {
			await onSubmit(payload)
		} catch (error) {
			console.error('Form submission failed:', error)
		}
	}

	return (
		<FormProvider {...form}>
			{mode === 'edit' && (
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
					<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
					<div className="text-amber-800 dark:text-amber-200">
						<p className="font-medium">
							การแก้ไขนี้จะมีผลกับแรงค์ที่ใช้เช็คอินรายวันนี้อยู่เท่านั้น
						</p>
						<p className="mt-0.5">
							กรุณาตรวจสอบให้แน่ใจว่าการเปลี่ยนแปลงนี้ถูกต้องสำหรับทุกแรงค์ที่ใช้งานอยู่
						</p>
					</div>
				</div>
			)}
			<form
				onSubmit={form.handleSubmit(handleSubmit, showFormValidationToast)}
				className="space-y-4 sm:space-y-8"
			>
				{/* Basic Information Section */}
				<div className="grid grid-cols-1 gap-3 lg:gap-4">
					{/* Basic Information */}
					<div className="space-y-4 rounded-lg border p-4 sm:space-y-6 sm:p-6">
						<h2 className="text-lg font-semibold sm:text-xl">ข้อมูลทั่วไป</h2>
						<div className="space-y-4">
							<NameField control={form.control} />

							<FormField
								control={form.control}
								name="days"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> จำนวนวันเช็คอิน
										</FormLabel>
										<Select
											value={String(field.value)}
											onValueChange={(value) => {
												const numValue = Number(value)
												field.onChange(numValue)
												handleDaysChange(numValue)
											}}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกจำนวนวัน" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{Array.from(
													{
														length:
															MAX_DAILY_CHECK_IN_DAYS -
															MIN_DAILY_CHECK_IN_DAYS +
															1,
													},
													(_, i) => i + MIN_DAILY_CHECK_IN_DAYS,
												).map((day) => (
													<SelectItem key={day} value={String(day)}>
														{day} วัน
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							{mode === 'edit' && defaultValues ? (
								<div className="rounded-lg border p-4">
									<RequireConnectedLineDailyCheckIn
										dailyCheckInData={
											defaultValues as GetDailyCheckInByIdResponse
										}
										mode={mode}
										onUpdate={(requireConnectedLine) =>
											form.setValue(
												'requireConnectedLine',
												requireConnectedLine,
											)
										}
									/>
								</div>
							) : (
								<FormField
									control={form.control}
									name="requireConnectedLine"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
											<div className="space-y-0.5">
												<FormLabel className="text-base">
													ต้องเชื่อมต่อ LINE
												</FormLabel>
												<p className="text-sm text-muted-foreground">
													ผู้เล่นต้องเชื่อมต่อบัญชี LINE
													ก่อนจึงจะสามารถเข้าร่วมกิจกรรมได้
												</p>
											</div>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							)}
						</div>
					</div>
				</div>

				{/* Check-in Settings Section */}
				<div className="rounded-lg border p-4 sm:p-6">
					<div className="mb-4 flex items-center justify-between sm:mb-6">
						<h2 className="text-lg font-semibold sm:text-xl">
							ตั้งค่าการเช็คอิน
						</h2>
						<div className="flex items-center gap-3">
							<Button
								type="button"
								size="sm"
								onClick={() => setIsTemplateModalOpen(true)}
								className="gap-1.5 bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-sm hover:from-violet-600 hover:to-indigo-600"
							>
								<Wand2 className="h-3.5 w-3.5" />
								สร้างจากเทมเพลต
							</Button>
							<span className="text-sm text-muted-foreground">
								{watchedDays} วัน
							</span>
						</div>
					</div>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
						{Array.from({ length: watchedDays }, (_, index) => (
							<Card key={index} className="border-2 p-3 sm:p-4">
								<div className="mb-3 flex items-center justify-between border-b pb-2 sm:mb-4">
									<h3 className="text-base font-medium sm:text-lg">
										วันที่ {index + 1}
									</h3>
								</div>
								<div className="space-y-3 sm:space-y-4">
									<FormField
										control={form.control}
										name={`checkInSetting.${index}.rewardType`}
										render={({ field }) => (
											<FormItem>
												<FormLabel className="text-sm sm:text-base">
													<RequiredIndicator /> ประเภทรางวัล
												</FormLabel>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
												>
													<FormControl>
														<SelectTrigger className="text-sm sm:text-base">
															<SelectValue placeholder="เลือกประเภทรางวัล" />
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														{Object.values(CheckInRewardType).map((type) => (
															<SelectItem key={type} value={type}>
																{DAILY_CHECK_IN_REWARD_TYPE_LABELS[type]}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name={`checkInSetting.${index}.amount`}
										render={({ field }) => {
											const isFreeSpin =
												form.watch(`checkInSetting.${index}.rewardType`) ===
												CheckInRewardType.FREE_SPIN
											return (
												<FormItem>
													<FormLabel className="text-sm sm:text-base">
														<RequiredIndicator /> จำนวนรางวัล
														{isFreeSpin && (
															<p className="text-xs text-muted-foreground">
																ฟรีสปินจำกัดจำนวนรางวัลที่ 1 อันเท่านั้น
															</p>
														)}
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="กรอกจำนวนรางวัล"
															{...field}
															value={isFreeSpin ? 1 : field.value}
															disabled={isFreeSpin}
															onChange={(e) =>
																field.onChange(
																	e.target.value
																		? Number(e.target.value)
																		: null,
																)
															}
															className="text-sm sm:text-base"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)
										}}
									/>

									<div className="space-y-3 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0">
										<FormField
											control={form.control}
											name={`checkInSetting.${index}.minimumTurnoverAmount`}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm sm:text-base">
														ยอดเทิร์นขั้นต่ำ (จำนวนเครดิต)
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="กรอกยอดเทิร์นขั้นต่ำ"
															{...field}
															value={field.value ?? ''}
															onChange={(e) =>
																field.onChange(
																	e.target.value
																		? Number(e.target.value)
																		: null,
																)
															}
															className="text-sm sm:text-base"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name={`checkInSetting.${index}.minimumDepositAmount`}
											render={({ field }) => (
												<FormItem>
													<FormLabel className="text-sm sm:text-base">
														ยอดฝากขั้นต่ำ
													</FormLabel>
													<FormControl>
														<Input
															type="number"
															placeholder="กรอกยอดฝากขั้นต่ำ"
															{...field}
															value={field.value ?? ''}
															onChange={(e) =>
																field.onChange(
																	e.target.value
																		? Number(e.target.value)
																		: null,
																)
															}
															className="text-sm sm:text-base"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									{/* FREE_SPIN specific fields */}
									{form.watch(`checkInSetting.${index}.rewardType`) ===
										CheckInRewardType.FREE_SPIN && (
										<div className="space-y-3 border-t pt-3">
											<h4 className="text-sm font-medium text-muted-foreground">
												ตั้งค่าฟรีสปิน
											</h4>
											<div className="space-y-3">
												<FormField
													control={form.control}
													name={`checkInSetting.${index}.presetId`}
													render={({ field }) => (
														<FormItem>
															<FormLabel className="text-sm sm:text-base">
																<RequiredIndicator /> RTP Preset
															</FormLabel>
															<Select
																onValueChange={field.onChange}
																value={field.value}
															>
																<FormControl>
																	<SelectTrigger className="text-sm sm:text-base">
																		<SelectValue placeholder="เลือก RTP Preset" />
																	</SelectTrigger>
																</FormControl>
																<SelectContent>
																	{presets?.presets?.map((preset) => (
																		<SelectItem
																			key={preset.id}
																			value={preset.id}
																		>
																			{preset.note}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<FormMessage />
														</FormItem>
													)}
												/>

												<FormField
													control={form.control}
													name={`checkInSetting.${index}.gameId`}
													render={({ field }) => (
														<FormItem>
															<FormLabel className="text-sm sm:text-base">
																<RequiredIndicator /> เกม
															</FormLabel>
															<Select
																onValueChange={field.onChange}
																value={field.value}
															>
																<FormControl>
																	<SelectTrigger className="text-sm sm:text-base">
																		<SelectValue placeholder="เลือกเกม" />
																	</SelectTrigger>
																</FormControl>
																<SelectContent>
																	{filteredGames?.map((game) => (
																		<SelectItem key={game.id} value={game.id}>
																			{game.name}
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
															<FormMessage />
														</FormItem>
													)}
												/>

												<FormField
													control={form.control}
													name={`checkInSetting.${index}.betAmount`}
													render={({ field }) => (
														<FormItem>
															<FormLabel className="text-sm sm:text-base">
																<RequiredIndicator /> จำนวนเดิมพัน
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	placeholder="กรอกจำนวนเดิมพัน"
																	{...field}
																	onChange={(e) =>
																		field.onChange(
																			e.target.value
																				? Number(e.target.value)
																				: null,
																		)
																	}
																	className="text-sm sm:text-base"
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
										</div>
									)}
								</div>
							</Card>
						))}
					</div>
				</div>

				{/* Wallet Condition Section */}
				<WalletConditionSection<DailyCheckInFormValues>
					control={form.control}
					fieldPrefix="walletCondition"
					showGameSettings={true}
					showPoolPreset={true}
				/>

				{/* Action Buttons */}
				<div className="flex flex-col gap-3 pb-4 sm:flex-row sm:gap-4 sm:pb-8">
					<Button
						type="button"
						variant="default"
						size="lg"
						className="w-full"
						onClick={() => {
							form.handleSubmit(
								() => setShowRtpConfirm(true),
								showFormValidationToast,
							)()
						}}
					>
						{mode === 'create' ? 'สร้างโปรโมชั่น' : 'แก้ไขโปรโมชั่น'}
					</Button>
					<Button
						type="button"
						variant="outline"
						size="lg"
						className="w-full"
						onClick={() => navigate(-1)}
					>
						ยกเลิก
					</Button>
				</div>
			</form>
			<RtpConfirmDialog
				open={showRtpConfirm}
				onOpenChange={setShowRtpConfirm}
				onConfirm={() => {
					setShowRtpConfirm(false)
					form.handleSubmit(
						(values) => handleSubmit(values),
						showFormValidationToast,
					)()
				}}
				confirmLabel={mode === 'create' ? 'สร้างโปรโมชั่น' : 'แก้ไขโปรโมชั่น'}
			/>
			<AlertDialog
				open={pendingPayload !== null}
				onOpenChange={(open) => {
					if (!open) setPendingPayload(null)
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							ยืนยันการสร้างกิจกรรมเช็คอินรายวัน
						</AlertDialogTitle>
						<AlertDialogDescription className="space-y-2">
							<span className="block">
								รายการเช็คอินรายวันที่สร้างใหม่จะมีผลต่อผู้เล่นเมื่อถูกเชื่อมกับแรงค์ที่ต้องการแล้วเท่านั้น
							</span>
							<span className="block font-medium text-foreground">
								หลังจากสร้างแล้ว อย่าลืมไปที่หน้า <strong>จัดการแรงค์</strong>{' '}
								เพื่อเชื่อมรายการเช็คอินรายวันนี้ให้กับแรงค์ที่ต้องการ
							</span>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setPendingPayload(null)}>
							กลับไปแก้ไข
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={async () => {
								if (pendingPayload) {
									try {
										await onSubmit(pendingPayload)
									} catch (error) {
										console.error('Form submission failed:', error)
									} finally {
										setPendingPayload(null)
									}
								}
							}}
						>
							สร้างกิจกรรม
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
			<GenerateTemplateModal
				open={isTemplateModalOpen}
				onOpenChange={setIsTemplateModalOpen}
				onApply={(generated) => {
					const newDays = generated.length
					form.setValue('days', newDays)
					form.setValue('checkInSetting', generated)
					// Also sync the snapshot so shrinking/growing preserves the generated data
					generated.forEach((s) => settingsSnapshotRef.current.set(s.day, s))
				}}
			/>
		</FormProvider>
	)
}
